/**
 * OwnerLineChart — lightweight inline SVG line chart for the Owner Console kit.
 *
 * Props:
 *   points      — { label: string; value: number }[]  values in [0, 100]
 *   ariaLabel   — accessible label for the chart image
 *   emptyText   — text to render when there are no points
 *
 * Renders a fixed-viewBox polyline (values clamped to 0–100).
 * Uses only CSS variables from the existing Owner Console token set;
 * no inline hex or rgba colours.
 */

const VIEW_W = 300
const VIEW_H = 80
const PAD_X = 2
const PAD_Y = 6

/** Clamp a value to the 0–100 range. */
function clamp(v: number): number {
  if (!Number.isFinite(v)) return 0
  return Math.min(100, Math.max(0, v))
}

/** Map a [0–100] value to SVG y coordinate (top = 0 in SVG). */
function toSvgY(value: number): number {
  const usable = VIEW_H - 2 * PAD_Y
  return PAD_Y + usable - (clamp(value) / 100) * usable
}

/** Map an index to SVG x coordinate. */
function toSvgX(index: number, total: number): number {
  const usable = VIEW_W - 2 * PAD_X
  if (total <= 1) return VIEW_W / 2
  return PAD_X + (index / (total - 1)) * usable
}

export interface OwnerLineChartPoint {
  label: string
  value: number
}

export interface OwnerLineChartProps {
  points: OwnerLineChartPoint[]
  ariaLabel: string
  emptyText: string
}

export function OwnerLineChart({ points, ariaLabel, emptyText }: OwnerLineChartProps) {
  if (points.length === 0) {
    return (
      <p className="ownerLineChart__empty" aria-label={ariaLabel}>
        {emptyText}
      </p>
    )
  }

  const clamped = points.map((p) => ({ ...p, value: clamp(p.value) }))

  // Build polyline points string
  const svgPoints = clamped
    .map((p, i) => `${toSvgX(i, clamped.length)},${toSvgY(p.value)}`)
    .join(' ')

  const firstLabel = clamped[0].label
  const lastLabel = clamped[clamped.length - 1].label
  const latestValue = clamped[clamped.length - 1].value

  return (
    <div className="ownerLineChart" aria-label={ariaLabel} role="img">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        className="ownerLineChart__svg"
        aria-hidden="true"
      >
        {/* Baseline at y=0% (bottom) */}
        <line
          x1={PAD_X}
          y1={toSvgY(0)}
          x2={VIEW_W - PAD_X}
          y2={toSvgY(0)}
          className="ownerLineChart__baseline"
        />
        {/* Main line */}
        {clamped.length === 1 ? (
          // Single point: render a dot
          <circle
            cx={toSvgX(0, 1)}
            cy={toSvgY(clamped[0].value)}
            r={3}
            className="ownerLineChart__dot"
          />
        ) : (
          <polyline
            points={svgPoints}
            className="ownerLineChart__line"
            fill="none"
          />
        )}
        {/* Latest value dot */}
        {clamped.length > 1 && (
          <circle
            cx={toSvgX(clamped.length - 1, clamped.length)}
            cy={toSvgY(latestValue)}
            r={3}
            className="ownerLineChart__dot"
          />
        )}
      </svg>
      {/* Date labels */}
      <div className="ownerLineChart__labels">
        <span className="ownerLineChart__labelStart">{firstLabel}</span>
        <span className="ownerLineChart__labelLatest">{latestValue.toFixed(1)}%</span>
        <span className="ownerLineChart__labelEnd">{lastLabel}</span>
      </div>
    </div>
  )
}
